import React, {
  useState, useRef, useCallback, useEffect, useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LandingLayout from './components/LandingLayout';
import SocialProofBar from './components/SocialProofBar';
import { HeartImg } from 'src/assets/images';
import { track, Events } from 'src/utils/analytics';
import * as AssistantApi from 'src/api/Assistant';

// ─── Constants ────────────────────────────────────────────────────────────────

const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_SM = '2px 2px 0px #1e1e1e';

const HEART_WORDS = [
  'because', 'i believe', 'however', 'therefore', 'in my opinion',
  'for example', 'i think', 'i feel', 'on the other hand', 'in conclusion',
  'first of all', 'most importantly', 'i wonder', 'this shows', 'as a result',
  'in addition', 'for instance', 'i agree', 'i disagree', 'furthermore',
];

// Canned Writing Buddy responses used as fallback when API is unavailable
const BUDDY_FALLBACKS = [
  "Great start! Can you add a specific example to support your claim? Think about something you've seen or experienced.",
  "Nice work! Your hook is strong. Now try adding a reason WHY you believe this. Use the word 'because' to connect your idea.",
  "I love that you shared your opinion! What evidence could make your argument even stronger?",
  "You're doing great! Try reading your claim out loud — does it sound convincing? What could make it even clearer?",
];

// ─── Contained Celebrations ──────────────────────────────────────────────────
// These render inside the notebook window (position:absolute) rather than
// portalling to document.body, so animations stay clipped to the window.

const HEART_KEYFRAMES_ID = 'pressto-heart-notebook-keyframes';
function ensureHeartKeyframes() {
  if (typeof document === 'undefined' || document.getElementById(HEART_KEYFRAMES_ID)) return;
  const s = document.createElement('style');
  s.id = HEART_KEYFRAMES_ID;
  s.textContent = `
    @keyframes nb-heart-float {
      0%   { transform: translate(var(--hx0,0px), 0) rotate(var(--hr0,0deg)) scale(0.5); opacity:0; }
      10%  { opacity:1; transform: translate(var(--hx1,0px), -60px) rotate(var(--hr1,0deg)) scale(1); }
      88%  { opacity:1; transform: translate(var(--hx2,0px), -700px) rotate(var(--hr2,0deg)) scale(0.8); }
      100% { opacity:0; transform: translate(var(--hx2,0px), -800px) rotate(var(--hr2,0deg)) scale(0.6); }
    }
    @keyframes nb-confetti-fall {
      0%   { transform: translate(var(--cx,0px), -20px) rotate(0deg) scale(1); opacity:1; }
      100% { transform: translate(var(--cx,0px), 320px) rotate(var(--cr,360deg)) scale(0.6); opacity:0; }
    }
  `;
  document.head.appendChild(s);
}

function ContainedHearts({ bursts, containerHeight = 600 }: { bursts: Array<{ id: string }>; containerHeight?: number }) {
  useEffect(() => { ensureHeartKeyframes(); }, []);
  if (!bursts.length) return null;
  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 50 }}>
      {bursts.map(b =>
        Array.from({ length: 10 }, (_, i) => {
          const left = 5 + Math.random() * 90;
          const size = 20 + Math.random() * 20;
          const dur = 1.8 + Math.random() * 0.8;
          const delay = Math.random() * 0.3;
          return (
            <img key={`${b.id}-${i}`} src={HeartImg} alt="" style={{
              position: 'absolute', bottom: 0, left: `${left}%`,
              width: size, height: 'auto', pointerEvents: 'none',
              animation: `nb-heart-float ${dur}s ease-out ${delay}s forwards`,
              ['--hx0' as any]: `${(Math.random()-0.5)*30}px`,
              ['--hx1' as any]: `${(Math.random()-0.5)*80}px`,
              ['--hx2' as any]: `${(Math.random()-0.5)*140}px`,
              ['--hr0' as any]: `${(Math.random()-0.5)*30}deg`,
              ['--hr1' as any]: `${(Math.random()-0.5)*45}deg`,
              ['--hr2' as any]: `${(Math.random()-0.5)*60}deg`,
            }} />
          );
        })
      )}
    </Box>
  );
}

const CONFETTI_COLORS = ['#FFE082','#FF6B6B','#4CAF50','#2196F3','#FF4081','#FFB300','#9C27B0'];

function ContainedConfetti({ active }: { active: boolean }) {
  useEffect(() => { ensureHeartKeyframes(); }, []);
  const pieces = useMemo(() => Array.from({ length: 28 }, (_, i) => ({
    id: i,
    left: 5 + Math.random() * 90,
    size: 6 + Math.random() * 8,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    dur: 1.2 + Math.random() * 0.8,
    delay: Math.random() * 0.5,
    cx: `${(Math.random()-0.5)*80}px`,
    cr: `${(Math.random() > 0.5 ? 1 : -1) * (180 + Math.random()*180)}deg`,
  })), []);
  if (!active) return null;
  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 50 }}>
      {pieces.map(p => (
        <Box key={p.id} sx={{
          position: 'absolute', top: 0, left: `${p.left}%`,
          width: p.size, height: p.size * 0.6,
          backgroundColor: p.color, borderRadius: '2px',
          animation: `nb-confetti-fall ${p.dur}s ease-in ${p.delay}s forwards`,
          ['--cx' as any]: p.cx, ['--cr' as any]: p.cr,
        }} />
      ))}
    </Box>
  );
}

// ─── Mini CTA ─────────────────────────────────────────────────────────────────

function MiniCTA({ label, sublabel, to, location }: { label: string; sublabel?: string; to: '/register' | '/login'; location: string }) {
  const navigate = useNavigate();
  const isSignup = to === '/register';
  return (
    <Box
      onClick={() => { track(Events.ClickedCTA, { location, label, destination: to }); navigate(to); }}
      sx={{ mt: 2, p: 1.5, backgroundColor: isSignup ? '#2C2C2C' : '#F7F7F7', border: '2px solid #1e1e1e', borderRadius: '8px', boxShadow: SHADOW_SM, cursor: 'pointer', transition: 'all 80ms', '&:hover': { transform: 'translate(2px,2px)', boxShadow: '1px 1px 0 #1e1e1e' }, textAlign: 'center' }}
    >
      <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.8rem', color: isSignup ? '#FFE082' : '#2C2C2C' }}>
        {label} →
      </Typography>
      {sublabel && <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', color: isSignup ? 'rgba(255,255,255,0.6)' : '#888', mt: 0.25 }}>{sublabel}</Typography>}
    </Box>
  );
}

// ─── Draggable Window Shell ───────────────────────────────────────────────────

interface WindowState { id: string; x: number; y: number; open: boolean; zIndex: number; }

interface NotebookWindowProps {
  id: string; title: string; emoji: string; accentColor: string;
  state: WindowState; onClose: (id: string) => void;
  onFocus: (id: string) => void; onMove: (id: string, x: number, y: number) => void;
  children: React.ReactNode; width?: number; overlay?: React.ReactNode;
}

// Fire each homepage-interaction event at most once per page load (keyed by its
// props), so we capture "did they engage with X" without spamming on every
// keystroke or drag-move.
const firedHomepageEvents = new Set<string>();
function trackHomepageOnce(event: string, props: Record<string, string>) {
  const k = `${event}:${Object.values(props).join('|')}`;
  if (firedHomepageEvents.has(k)) return;
  firedHomepageEvents.add(k);
  track(event, props);
}

function NotebookWindow({ id, title, emoji, accentColor, state, onClose, onFocus, onMove, children, width = 340, overlay }: NotebookWindowProps) {
  const dragRef = useRef<{ startX: number; startY: number; winX: number; winY: number } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    onFocus(id);
    dragRef.current = { startX: e.clientX, startY: e.clientY, winX: state.x, winY: state.y };
    const handleDrag = (me: MouseEvent) => {
      if (!dragRef.current) return;
      if (Math.abs(me.clientX - dragRef.current.startX) + Math.abs(me.clientY - dragRef.current.startY) > 4) {
        trackHomepageOnce(Events.HomepageWindowDragged, { window: id });
      }
      onMove(id, dragRef.current.winX + me.clientX - dragRef.current.startX, dragRef.current.winY + me.clientY - dragRef.current.startY);
    };
    const handleUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('mouseup', handleUp);
  }, [id, state.x, state.y, onFocus, onMove]);

  if (!state.open) return null;

  return (
    <Box onClick={() => onFocus(id)} sx={{ position: 'absolute', left: state.x, top: state.y, width, zIndex: state.zIndex, borderRadius: '10px', border: '2px solid #1e1e1e', boxShadow: SHADOW, backgroundColor: '#FFFFFF', overflow: 'hidden', userSelect: 'none' }}>
      {overlay}
      <Box onMouseDown={handleMouseDown} sx={{ backgroundColor: accentColor, borderBottom: '2px solid #1e1e1e', px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '1rem' }}>{emoji}</Typography>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.8rem', color: '#1e1e1e' }}>{title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Box onClick={(e) => { e.stopPropagation(); track(Events.HomepageWindowClosed, { window: id }); onClose(id); }} sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#FF5F57', border: '1.5px solid #E0443E', cursor: 'pointer', '&:hover': { opacity: 0.8 } }} />
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#FEBC2E', border: '1.5px solid #D4A017' }} />
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#28C840', border: '1.5px solid #1DAD2B' }} />
        </Box>
      </Box>
      <Box sx={{ p: 2.5, maxHeight: 420, overflowY: 'auto' }}>{children}</Box>
    </Box>
  );
}

// ─── Notebook 1: Scaffolded Editor with Heart Words ───────────────────────────

function WritingEditorContent({ onBurst }: { onBurst?: () => void }) {
  const [activeBlock, setActiveBlock] = useState(0);
  const [texts, setTexts] = useState(['', '', '', '']);
  const [buddyMsg, setBuddyMsg] = useState('');
  const [buddyLoading, setBuddyLoading] = useState(false);
  const celebratedRef = useRef<Set<string>>(new Set());

  const blocks = [
    { label: 'Hook', color: '#E3F2FD', placeholder: 'Start with a question or surprising fact…' },
    { label: 'My Claim', color: '#F3E5F5', placeholder: 'I believe that…' },
    { label: 'My Reason', color: '#E8F5E9', placeholder: 'One reason is… because…' },
    { label: 'Conclusion', color: '#FFF3E0', placeholder: 'In my opinion, this matters because…' },
  ];

  const handleType = (val: string, idx: number) => {
    trackHomepageOnce(Events.HomepageDemoUsed, { demo: 'student_draft' });
    const next = [...texts];
    next[idx] = val;
    setTexts(next);

    const lower = val.toLowerCase();
    const newWords = HEART_WORDS.filter(w => lower.includes(w) && !celebratedRef.current.has(w));
    if (newWords.length > 0) {
      newWords.forEach(w => celebratedRef.current.add(w));
      onBurst?.();
    }
  };

  const askBuddy = async () => {
    if (texts.every(t => !t.trim())) return;
    trackHomepageOnce(Events.HomepageDemoUsed, { demo: 'student_draft', action: 'ask_buddy' });
    setBuddyLoading(true);
    setBuddyMsg('');
    try {
      const paragraphs = blocks.map((b, i) => ({ text: texts[i], blockName: b.label })).filter(p => p.text.trim());
      const res = await AssistantApi.askWritingBuddyDemo(
        paragraphs,
        blocks[activeBlock].label,
        'Opinion',
        blocks.map(b => b.label),
        'Write an opinion about something in your school.',
        '4th',
      );
      setBuddyMsg(res.data?.data?.[0]?.text || BUDDY_FALLBACKS[0]);
    } catch {
      setBuddyMsg(BUDDY_FALLBACKS[Math.floor(Math.random() * BUDDY_FALLBACKS.length)]);
    } finally {
      setBuddyLoading(false);
    }
  };

  const allText = texts.join(' ');
  const wordCount = allText.trim().split(/\s+/).filter(Boolean).length;
  const progress = Math.min(100, Math.round((wordCount / 60) * 100));

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Mini toolbar */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
        {['📝 Opinion'].map(t => (
          <Box key={t} sx={{ fontSize: '0.6rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, px: 1, py: 0.4, border: '1.5px solid #1e1e1e', borderRadius: '4px', backgroundColor: '#FFE082', boxShadow: '1px 1px 0 #1e1e1e' }}>{t}</Box>
        ))}
        <Box sx={{ fontSize: '0.6rem', fontFamily: 'Lato, sans-serif', color: '#888', ml: 'auto', alignSelf: 'center' }}>
          Heart words: <em>because, I believe, however, therefore, I think…</em> ❤️
        </Box>
      </Box>

      <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.82rem', mb: 1.5, color: '#2C2C2C' }}>
        My Opinion About School Lunch
      </Typography>

      {blocks.map((block, i) => (
        <Box
          key={block.label}
          onClick={() => setActiveBlock(i)}
          sx={{ mb: 1, borderRadius: '4px', border: `1.5px solid ${i === activeBlock ? '#1e1e1e' : '#ddd'}`, overflow: 'hidden', cursor: 'text', transition: 'border-color 100ms' }}
        >
          <Box sx={{ backgroundColor: block.color, px: 1, py: 0.5, borderBottom: `1px solid ${i === activeBlock ? '#ccc' : '#eee'}` }}>
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, fontFamily: 'Montserrat, sans-serif', color: '#666', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{block.label}</Typography>
          </Box>
          <Box
            component="textarea"
            value={texts[i]}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleType(e.target.value, i)}
            onFocus={() => setActiveBlock(i)}
            placeholder={block.placeholder}
            rows={2}
            sx={{ width: '100%', border: 'none', outline: 'none', resize: 'none', fontFamily: 'Lato, sans-serif', fontSize: { xs: '16px', md: '0.75rem' }, color: '#2C2C2C', p: 1, boxSizing: 'border-box', backgroundColor: '#fff', lineHeight: 1.5, '&::placeholder': { color: '#bbb' } }}
          />
        </Box>
      ))}

      {/* Progress bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ height: 4, flex: 1, backgroundColor: '#E0E0E0', borderRadius: 2 }}>
          <Box sx={{ height: 4, width: `${progress}%`, backgroundColor: progress === 100 ? '#4CAF50' : '#FFB300', borderRadius: 2, transition: 'width 300ms ease' }} />
        </Box>
        <Typography sx={{ fontSize: '0.6rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, color: '#888', whiteSpace: 'nowrap' }}>{wordCount} words</Typography>
      </Box>

      {/* Writing Buddy */}
      <Box
        onClick={!buddyLoading ? askBuddy : undefined}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, backgroundColor: '#2C2C2C', borderRadius: '6px', border: '1.5px solid #1e1e1e', boxShadow: SHADOW_SM, cursor: buddyLoading ? 'default' : 'pointer', transition: 'all 80ms', '&:hover': buddyLoading ? {} : { transform: 'translate(1px,1px)', boxShadow: '1px 1px 0 #1e1e1e' } }}
      >
        <Typography sx={{ fontSize: '1rem' }}>🤖</Typography>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.72rem', color: '#FFE082', flex: 1 }}>
          {buddyLoading ? 'Writing Buddy is thinking…' : 'Ask Writing Buddy'}
        </Typography>
        {buddyLoading && <CircularProgress size={12} sx={{ color: '#FFE082' }} />}
      </Box>

      {buddyMsg && (
        <>
          <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: '#F0FFF4', border: '1.5px solid #1e1e1e', borderRadius: '6px', boxShadow: SHADOW_SM }}>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.75rem', color: '#2C2C2C', lineHeight: 1.6 }}>
              💬 <strong>Buddy:</strong> {buddyMsg}
            </Typography>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', color: '#888', mt: 0.5 }}>
              <em>Keep typing — heart words get a surprise! ❤️</em>
            </Typography>
          </Box>
          <MiniCTA label="Save your writing — sign up free" sublabel="Free for students & teachers" to="/register" location="window_writing_buddy" />
        </>
      )}
    </Box>
  );
}

// ─── Notebook 2: Newspaper (reactive) ────────────────────────────────────────

function NewspaperContent() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [published, setPublished] = useState(false);
  const [confettiActive, setConfettiActive] = useState(false);

  const publish = () => {
    if (!title.trim()) return;
    trackHomepageOnce(Events.HomepageDemoUsed, { demo: 'newspaper' });
    setPublished(true);
    setConfettiActive(true);
    setTimeout(() => setConfettiActive(false), 2500);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <ContainedConfetti active={confettiActive} />
      {/* One-click pitch */}
      <Box sx={{ mb: 2, p: 1, backgroundColor: '#FFF9C4', border: '1.5px solid #1e1e1e', borderRadius: '6px', boxShadow: SHADOW_SM, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '1rem' }}>🗞️</Typography>
        <Box>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.7rem', color: '#2C2C2C' }}>One click = a real school newspaper</Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.62rem', color: '#555' }}>Type a headline below and hit Publish — your story goes live instantly.</Typography>
        </Box>
      </Box>

      <Typography sx={{ fontFamily: 'Bitter, Georgia, serif', fontSize: '1.2rem', fontWeight: 700, textAlign: 'center', borderBottom: '3px double #1a1a1a', pb: 1, mb: 1 }}>
        The Classroom Times
      </Typography>
      <Typography sx={{ fontFamily: 'Bitter, serif', fontSize: '0.55rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#888', mb: 2 }}>
        Spring Edition &bull; Room 214
      </Typography>

      {published ? (
        <>
          <Box sx={{ border: '1.5px solid #1e1e1e', borderRadius: '6px', p: 2, backgroundColor: '#FFF9C4', boxShadow: SHADOW_SM, mb: 2 }}>
            <Box sx={{ display: 'inline-block', backgroundColor: '#2C2C2C', color: '#fff', fontSize: '0.5rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, textTransform: 'uppercase', px: 0.75, py: 0.25, borderRadius: '2px', mb: 1 }}>✨ Published!</Box>
            <Typography sx={{ fontFamily: 'Bitter, serif', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3, mb: 0.75 }}>{title || 'Untitled Story'}</Typography>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.75rem', color: '#444', lineHeight: 1.5 }}>{body || 'Story content goes here…'}</Typography>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.6rem', fontWeight: 700, color: '#888', mt: 1 }}>By A. Student &bull; May 2026</Typography>
          </Box>
          <MiniCTA label="Start your school newspaper in one click" sublabel="Free for teachers — no setup required" to="/register" location="window_newspaper_publish" />
        </>
      ) : (
        <Box>
          <Box component="input" value={title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)} placeholder="Your story headline…" sx={{ width: '100%', fontFamily: 'Bitter, serif', fontWeight: 700, fontSize: { xs: '16px', md: '0.85rem' }, p: 1, border: '1.5px solid #1e1e1e', borderRadius: '4px', mb: 1, boxSizing: 'border-box', outline: 'none', '&:focus': { borderColor: '#FFB300' } }} />
          <Box component="textarea" value={body} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBody(e.target.value)} placeholder="Write your story here…" rows={4} sx={{ width: '100%', fontFamily: 'Lato, sans-serif', fontSize: { xs: '16px', md: '0.75rem' }, p: 1, border: '1.5px solid #1e1e1e', borderRadius: '4px', mb: 1.5, boxSizing: 'border-box', outline: 'none', resize: 'none', '&:focus': { borderColor: '#FFB300' } }} />
          <Box onClick={publish} sx={{ p: 1, backgroundColor: title.trim() ? '#2C2C2C' : '#ccc', color: '#FFE082', borderRadius: '6px', border: '1.5px solid #1e1e1e', boxShadow: title.trim() ? SHADOW_SM : 'none', textAlign: 'center', cursor: title.trim() ? 'pointer' : 'default', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.8rem', transition: 'all 80ms', '&:hover': title.trim() ? { transform: 'translate(1px,1px)', boxShadow: '1px 1px 0 #1e1e1e' } : {} }}>
            🗞️ Publish to The Classroom Times
          </Box>
        </Box>
      )}

      {/* Existing stories */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        {[
          { emoji: '🌱', title: 'School Garden Opens', tag: 'News' },
          { emoji: '💬', title: 'Why Recess Matters', tag: 'Opinion' },
        ].map(s => (
          <Box key={s.title} sx={{ p: 1, backgroundColor: '#F7F7F7', border: '1.5px solid #1e1e1e', borderRadius: '4px', boxShadow: '2px 2px 0 #1e1e1e' }}>
            <Typography sx={{ fontSize: '0.9rem', mb: 0.25 }}>{s.emoji}</Typography>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.65rem', fontWeight: 700, lineHeight: 1.3, mb: 0.25 }}>{s.title}</Typography>
            <Box sx={{ display: 'inline-block', backgroundColor: '#2C2C2C', color: '#fff', fontSize: '0.45rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, textTransform: 'uppercase', px: 0.5, py: 0.2, borderRadius: '2px' }}>{s.tag}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Notebook 3: Assign a Lesson ─────────────────────────────────────────────

const GRADE_LEVELS = ['K', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];

function AssignContent() {
  const [topic, setTopic] = useState('');
  const [grade, setGrade] = useState('4th');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [assigned, setAssigned] = useState(false);

  const generate = async () => {
    if (!topic.trim()) return;
    trackHomepageOnce(Events.HomepageDemoUsed, { demo: 'assign_lesson' });
    setLoading(true);
    setPrompt('');
    try {
      const res = await AssistantApi.suggestWritingPrompt(topic.trim(), grade);
      const text = res.data?.data?.[0]?.text || '';
      for (let i = 1; i <= text.length; i++) {
        await new Promise(r => setTimeout(r, 12));
        setPrompt(text.slice(0, i));
      }
    } catch {
      const fallback = `Write an opinion piece about "${topic}" for ${grade} grade. Use at least two reasons to support your argument, and include a strong conclusion.`;
      for (let i = 1; i <= fallback.length; i++) {
        await new Promise(r => setTimeout(r, 10));
        setPrompt(fallback.slice(0, i));
      }
    } finally {
      setLoading(false);
    }
  };

  const QUICK_TOPICS = ['School lunch rules', 'CCSS.ELA.W.4.1', 'Climate change', 'Homework policy'];

  return (
    <Box>
      <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.75rem', color: '#555', mb: 1 }}>
        Paste a standard, topic, or URL:
      </Typography>
      <Box component="input" value={topic} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setTopic(e.target.value); setPrompt(''); setAssigned(false); }} placeholder="e.g. CCSS.ELA.W.4.1 or school lunch" sx={{ width: '100%', fontFamily: 'Lato, sans-serif', fontSize: { xs: '16px', md: '0.8rem' }, p: 1, border: '2px solid #1e1e1e', borderRadius: '4px', backgroundColor: '#F7F7F7', mb: 1, boxSizing: 'border-box', outline: 'none', '&:focus': { backgroundColor: '#fff', borderColor: '#FFB300' } }} />

      {/* Grade level picker */}
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.65rem', color: '#888', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Grade level</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {GRADE_LEVELS.map(g => (
            <Box key={g} onClick={() => setGrade(g)} sx={{ fontSize: '0.65rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, px: 0.75, py: 0.35, border: '1.5px solid #1e1e1e', borderRadius: '4px', backgroundColor: grade === g ? '#2C2C2C' : '#F7F7F7', color: grade === g ? '#FFE082' : '#2C2C2C', cursor: 'pointer', boxShadow: grade === g ? '1px 1px 0 #1e1e1e' : 'none', transition: 'all 80ms' }}>{g}</Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
        {QUICK_TOPICS.map(t => (
          <Box key={t} onClick={() => { setTopic(t); setPrompt(''); setAssigned(false); }} sx={{ fontSize: '0.6rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, px: 0.75, py: 0.3, border: '1.5px solid #1e1e1e', borderRadius: '4px', backgroundColor: '#F7F7F7', cursor: 'pointer', '&:hover': { backgroundColor: '#FFE082' }, transition: 'background 80ms' }}>{t}</Box>
        ))}
      </Box>

      <Box onClick={!loading ? generate : undefined} sx={{ p: 1, backgroundColor: topic.trim() ? '#2C2C2C' : '#ccc', color: '#FFE082', borderRadius: '6px', border: '1.5px solid #1e1e1e', boxShadow: topic.trim() ? SHADOW_SM : 'none', textAlign: 'center', cursor: topic.trim() && !loading ? 'pointer' : 'default', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.75rem', mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, transition: 'all 80ms', '&:hover': topic.trim() && !loading ? { transform: 'translate(1px,1px)', boxShadow: '1px 1px 0 #1e1e1e' } : {} }}>
        {loading ? <><CircularProgress size={10} sx={{ color: '#FFE082' }} /> Generating for {grade} grade…</> : `✨ Generate ${grade} grade prompt`}
      </Box>

      {prompt && (
        <Box sx={{ backgroundColor: '#F0FFF4', border: '1.5px solid #1e1e1e', borderRadius: '6px', p: 1.5, boxShadow: SHADOW_SM, mb: 1.5 }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.6rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>✨ Writing prompt</Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.78rem', color: '#2C2C2C', lineHeight: 1.6 }}>{prompt}</Typography>
          {!loading && (
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap' }}>
              {['Opinion', 'Grade 4', 'Sentence starters included'].map(tag => (
                <Box key={tag} sx={{ fontSize: '0.55rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, px: 0.75, py: 0.3, backgroundColor: '#2C2C2C', color: '#fff', borderRadius: '3px' }}>{tag}</Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {prompt && !loading && !assigned && (
        <Box onClick={() => setAssigned(true)} sx={{ p: 1, backgroundColor: '#4CAF50', color: '#fff', borderRadius: '6px', border: '1.5px solid #1e1e1e', boxShadow: SHADOW_SM, textAlign: 'center', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.75rem', transition: 'all 80ms', '&:hover': { transform: 'translate(1px,1px)', boxShadow: '1px 1px 0 #1e1e1e' } }}>
          📋 Assign to class →
        </Box>
      )}

      {assigned && (
        <>
          <Box sx={{ p: 1.5, backgroundColor: '#E8F5E9', border: '1.5px solid #1e1e1e', borderRadius: '6px', boxShadow: SHADOW_SM, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.8rem', color: '#2E7D32' }}>✅ Assigned to Period 3!</Typography>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', color: '#555', mt: 0.5 }}>Students can start writing now.</Typography>
          </Box>
          <MiniCTA label="Assign to your real class" sublabel="Free for teachers" to="/register" location="window_assign_cta" />
        </>
      )}
    </Box>
  );
}

// ─── Notebook 4: Teacher Dashboard preview ───────────────────────────────────

function DashboardContent() {
  const [selected, setSelected] = useState<string | null>(null);

  const students = [
    {
      name: 'Amara J.', words: 82, status: 'submitted',
      pastedWords: 0, flagged: false,
      preview: 'I believe our school should have longer recess because exercise helps students focus. One reason is that after running outside, I can sit still for much longer…',
      blocks: [{ name: 'Hook', typed: 18, pasted: 0 }, { name: 'Claim', typed: 22, pasted: 0 }, { name: 'Reason', typed: 42, pasted: 0 }],
    },
    {
      name: 'Marcus T.', words: 54, status: 'in progress',
      pastedWords: 0, flagged: false,
      preview: 'Have you ever wondered why homework feels so hard? I think homework should be optional because kids need time to rest…',
      blocks: [{ name: 'Hook', typed: 20, pasted: 0 }, { name: 'Claim', typed: 34, pasted: 0 }, { name: 'Reason', typed: 0, pasted: 0 }],
    },
    {
      name: 'Sofia R.', words: 103, status: 'submitted',
      pastedWords: 89, flagged: true,
      preview: 'In my opinion, the cafeteria should serve healthier options. For example, instead of pizza every Friday, we could have a salad bar with fresh vegetables from local farms…',
      blocks: [{ name: 'Hook', typed: 8, pasted: 0 }, { name: 'Claim', typed: 6, pasted: 47 }, { name: 'Reason', typed: 0, pasted: 42 }],
    },
    {
      name: 'Liam K.', words: 12, status: 'not started',
      pastedWords: 0, flagged: false,
      preview: '',
      blocks: [],
    },
  ];

  const flaggedCount = students.filter(s => s.flagged).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.8rem', color: '#2C2C2C' }}>Period 3 — Opinion Writing</Typography>
        <Box sx={{ fontSize: '0.6rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, px: 0.75, py: 0.3, backgroundColor: '#4CAF50', color: '#fff', borderRadius: '3px' }}>3/4 submitted</Box>
      </Box>

      {/* Show Work summary */}
      <Box sx={{ mb: 1.5, p: 1, backgroundColor: flaggedCount > 0 ? '#FFF3E0' : '#F0FFF4', border: `1.5px solid ${flaggedCount > 0 ? '#FFB300' : '#4CAF50'}`, borderRadius: '6px', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '0.85rem' }}>{flaggedCount > 0 ? '🚩' : '✅'}</Typography>
        <Box>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.65rem', color: '#2C2C2C' }}>
            Show Work{flaggedCount > 0 ? ` — ${flaggedCount} student flagged for AI/copy-paste` : ' — all students typed their own work'}
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.6rem', color: '#888' }}>
            Click a student to see paste activity per block
          </Typography>
        </Box>
      </Box>

      {students.map(s => (
        <Box
          key={s.name}
          onClick={() => { trackHomepageOnce(Events.HomepageDemoUsed, { demo: 'class_progress' }); setSelected(selected === s.name ? null : s.name); }}
          sx={{ mb: 1, p: 1.25, border: `1.5px solid ${s.flagged ? '#FFB300' : selected === s.name ? '#1e1e1e' : '#ddd'}`, borderRadius: '6px', cursor: 'pointer', backgroundColor: s.flagged ? '#FFFDE7' : selected === s.name ? '#FFF9C4' : '#FAFAFA', transition: 'all 80ms', '&:hover': { borderColor: '#1e1e1e', backgroundColor: '#F7F7F7' } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: s.flagged ? '#FFE082' : '#E3F2FD', border: `1.5px solid ${s.flagged ? '#FFB300' : '#1e1e1e'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, fontFamily: 'Montserrat, sans-serif' }}>
                {s.flagged ? '🚩' : s.name[0]}
              </Box>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.78rem' }}>{s.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', color: '#888' }}>{s.words}w</Typography>
              {s.pastedWords > 0 && (
                <Box sx={{ fontSize: '0.55rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, px: 0.75, py: 0.2, backgroundColor: '#FFB300', border: '1px solid #1e1e1e', borderRadius: '3px', color: '#2C2C2C' }}>{s.pastedWords}w pasted</Box>
              )}
              <Box sx={{ fontSize: '0.55rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, px: 0.75, py: 0.2, backgroundColor: s.status === 'submitted' ? '#E8F5E9' : s.status === 'in progress' ? '#FFF9C4' : '#F5F5F5', border: '1px solid #ddd', borderRadius: '3px', color: s.status === 'submitted' ? '#2E7D32' : s.status === 'in progress' ? '#F57F17' : '#999', textTransform: 'capitalize' }}>{s.status}</Box>
            </Box>
          </Box>

          {selected === s.name && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
              {s.preview && (
                <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.72rem', color: '#444', lineHeight: 1.55, fontStyle: 'italic', mb: 1 }}>
                  &ldquo;{s.preview}&rdquo;
                </Typography>
              )}
              {s.blocks.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {s.blocks.map(b => (
                    <Box key={b.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.6rem', fontWeight: 700, color: '#888', width: 44 }}>{b.name}</Typography>
                      <Box sx={{ flex: 1, height: 6, backgroundColor: '#F0F0F0', borderRadius: 3, overflow: 'hidden', display: 'flex' }}>
                        <Box sx={{ height: '100%', width: `${Math.min(100, (b.typed / 50) * 100)}%`, backgroundColor: '#4CAF50', borderRadius: 3 }} />
                        {b.pasted > 0 && <Box sx={{ height: '100%', width: `${Math.min(100, (b.pasted / 50) * 100)}%`, backgroundColor: '#FFB300' }} />}
                      </Box>
                      {b.pasted > 0 && <Typography sx={{ fontSize: '0.55rem', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, color: '#E65100' }}>{b.pasted}w pasted</Typography>}
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4CAF50' }} /><Typography sx={{ fontSize: '0.55rem', fontFamily: 'Lato, sans-serif', color: '#888' }}>typed</Typography></Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FFB300' }} /><Typography sx={{ fontSize: '0.55rem', fontFamily: 'Lato, sans-serif', color: '#888' }}>pasted</Typography></Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      ))}
      <MiniCTA label="See your real students' work" sublabel="Already a teacher? Log in →" to="/login" location="window_dashboard_cta" />
    </Box>
  );
}

// ─── Dock ─────────────────────────────────────────────────────────────────────

function Dock({ windows, onOpen }: { windows: { id: string; emoji: string; title: string; open: boolean }[]; onOpen: (id: string) => void }) {
  const closed = windows.filter(w => !w.open);
  if (!closed.length) return null;
  return (
    <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1.5, backgroundColor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', border: '2px solid #1e1e1e', borderRadius: '12px', px: 2, py: 1, boxShadow: SHADOW, zIndex: 100 }}>
      {closed.map(w => (
        <Box key={w.id} onClick={() => onOpen(w.id)} title={w.title} sx={{ width: 40, height: 40, borderRadius: '8px', border: '1.5px solid #1e1e1e', backgroundColor: '#F7F7F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', cursor: 'pointer', boxShadow: SHADOW_SM, transition: 'all 80ms', '&:hover': { transform: 'translateY(-4px)', boxShadow: '2px 6px 0 #1e1e1e' } }}>
          {w.emoji}
        </Box>
      ))}
    </Box>
  );
}

// ─── Desktop ──────────────────────────────────────────────────────────────────

const WINDOW_DEFS = [
  { id: 'writing',   title: 'Student draft — Opinion', emoji: '✏️', accentColor: '#E3F2FD', width: 350 },
  { id: 'newspaper', title: 'The Classroom Times',     emoji: '🗞️', accentColor: '#FFF9C4', width: 330 },
  { id: 'assign',    title: 'Assign a lesson',         emoji: '📋', accentColor: '#F3E5F5', width: 320 },
  { id: 'dashboard', title: 'Class progress',          emoji: '📊', accentColor: '#E8F5E9', width: 310 },
];

const INITIAL_WINS: WindowState[] = [
  { id: 'writing',   x: 10,  y: 20,  open: true, zIndex: 1 },
  { id: 'newspaper', x: 370, y: 10,  open: true, zIndex: 2 },
  { id: 'assign',    x: 30,  y: 380, open: true, zIndex: 3 },
  { id: 'dashboard', x: 390, y: 350, open: true, zIndex: 4 },
];

function Desktop() {
  const [wins, setWins] = useState(INITIAL_WINS);
  const maxZ = useRef(10);
  const [writingBursts, setWritingBursts] = useState<Array<{ id: string }>>([]);

  const handleClose  = useCallback((id: string) => setWins(p => p.map(w => w.id === id ? { ...w, open: false } : w)), []);
  const handleOpen   = useCallback((id: string) => { maxZ.current++; setWins(p => p.map(w => w.id === id ? { ...w, open: true, zIndex: maxZ.current } : w)); }, []);
  const handleFocus  = useCallback((id: string) => { maxZ.current++; setWins(p => p.map(w => w.id === id ? { ...w, zIndex: maxZ.current } : w)); }, []);
  const handleMove   = useCallback((id: string, x: number, y: number) => setWins(p => p.map(w => w.id === id ? { ...w, x, y } : w)), []);
  const handleWritingBurst = useCallback(() => {
    const id = `burst-${Date.now()}-${Math.random()}`;
    setWritingBursts(b => [...b, { id }]);
    setTimeout(() => setWritingBursts(b => b.filter(x => x.id !== id)), 4500);
  }, []);

  const dockItems = useMemo(() => WINDOW_DEFS.map(d => ({ ...d, open: wins.find(w => w.id === d.id)?.open ?? true })), [wins]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 700 }}>
      {WINDOW_DEFS.map(def => {
        const state = wins.find(w => w.id === def.id)!;
        return (
          <NotebookWindow key={def.id} id={def.id} title={def.title} emoji={def.emoji} accentColor={def.accentColor} state={state} onClose={handleClose} onFocus={handleFocus} onMove={handleMove} width={def.width} overlay={def.id === 'writing' ? <ContainedHearts bursts={writingBursts} /> : undefined}>
            {def.id === 'writing'   && <WritingEditorContent onBurst={handleWritingBurst} />}
            {def.id === 'newspaper' && <NewspaperContent />}
            {def.id === 'assign'    && <AssignContent />}
            {def.id === 'dashboard' && <DashboardContent />}
          </NotebookWindow>
        );
      })}
      <Dock windows={dockItems} onOpen={handleOpen} />
    </Box>
  );
}

// ─── Mobile: static stacked demos (no dragging — touch friendly) ─────────────
// On phones the draggable "desktop" makes no sense and its mouse-only drag
// handlers don't fire on touch. Render the same interactive content panes as a
// plain vertical stack of cards instead. The content inside each pane (typing
// heart words, generating prompts, tapping a student) is ordinary React state
// and works fine with touch.

function MobileDemoStack() {
  const [writingBursts, setWritingBursts] = useState<Array<{ id: string }>>([]);
  const handleWritingBurst = useCallback(() => {
    const id = `burst-${Date.now()}-${Math.random()}`;
    setWritingBursts(b => [...b, { id }]);
    setTimeout(() => setWritingBursts(b => b.filter(x => x.id !== id)), 4500);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {WINDOW_DEFS.map(def => (
        <Box key={def.id} sx={{ position: 'relative', borderRadius: '10px', border: '2px solid #1e1e1e', boxShadow: SHADOW, backgroundColor: '#FFFFFF', overflow: 'hidden' }}>
          {def.id === 'writing' && <ContainedHearts bursts={writingBursts} />}
          <Box sx={{ backgroundColor: def.accentColor, borderBottom: '2px solid #1e1e1e', px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '1rem' }}>{def.emoji}</Typography>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#1e1e1e' }}>{def.title}</Typography>
          </Box>
          <Box sx={{ position: 'relative', overflow: 'hidden', p: 2.5 }}>
            {def.id === 'writing'   && <WritingEditorContent onBurst={handleWritingBurst} />}
            {def.id === 'newspaper' && <NewspaperContent />}
            {def.id === 'assign'    && <AssignContent />}
            {def.id === 'dashboard' && <DashboardContent />}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LandingPageV4() {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  return (
    <LandingLayout>
      <Box sx={{ backgroundColor: '#FFFFFF', borderBottom: '2px solid #1e1e1e', overflow: 'hidden', minHeight: { xs: 'auto', md: 800 }, position: 'relative' }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 3, md: 6 }, pt: { xs: 6, md: 10 }, pb: { xs: 4, md: 6 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '400px 1fr' }, gap: { xs: 6, md: 8 }, alignItems: 'flex-start' }}>

          {/* Left: copy */}
          <Box sx={{ position: 'relative', zIndex: 10 }}>
            <Box sx={{ display: 'inline-block', backgroundColor: '#FFF9C4', border: '2px solid #1e1e1e', borderRadius: '4px', px: 1.5, py: 0.5, mb: 3, boxShadow: SHADOW_SM }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>The K–12 writing platform</Typography>
            </Box>

            <Typography variant="h1" sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: { xs: '2.4rem', md: '3.25rem' }, fontWeight: 800, lineHeight: 1.1, mb: 3, color: '#2C2C2C', letterSpacing: '-0.02em' }}>
              The writing platform that{' '}
              <Box component="span" sx={{ textDecoration: 'underline', textDecorationColor: '#FFB300', textUnderlineOffset: '6px', textDecorationThickness: '4px' }}>
                kids and teachers love
              </Box>
            </Typography>

            <Box sx={{ mb: 4 }}>
              {[
                { emoji: '📋', text: 'Align writing to any lesson or curriculum' },
                { emoji: '✏️', text: 'Scaffold students with structure, prompts & sentence starters' },
                { emoji: '🚩', text: 'Detect AI & plagiarism through behavior — not guesswork' },
                { emoji: '🗞️', text: 'Launch a classroom newspaper in one click' },
                { emoji: '🤖', text: 'AI that coaches students — never writes for them' },
              ].map(item => (
                <Box key={item.emoji} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1 }}>
                  <Typography sx={{ fontSize: '1rem', mt: '1px', flexShrink: 0 }}>{item.emoji}</Typography>
                  <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1rem', lineHeight: 1.55, color: '#444' }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <MuiButton variant="contained" onClick={() => { track(Events.ClickedCTA, { location: 'hero_v4', label: 'Try it free', destination: '/register' }); navigate('/register'); }} sx={{ backgroundColor: '#2C2C2C', color: '#fff', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: '6px', border: '2px solid #1e1e1e', boxShadow: SHADOW, px: 4, py: 1.5, '&:hover': { backgroundColor: '#1e1e1e', boxShadow: SHADOW_SM, transform: 'translate(2px,2px)' }, transition: 'all 100ms' }}>
                Try it free →
              </MuiButton>
              <MuiButton variant="outlined" onClick={() => { track(Events.ClickedCTA, { location: 'hero_v4', label: 'Contact us', destination: '/contact' }); navigate('/contact'); }} sx={{ borderColor: '#2C2C2C', border: '2px solid #2C2C2C', color: '#2C2C2C', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: '6px', boxShadow: SHADOW, px: 4, py: 1.5, backgroundColor: '#fff', '&:hover': { backgroundColor: '#F5F5F5', boxShadow: SHADOW_SM, transform: 'translate(2px,2px)' }, transition: 'all 100ms' }}>
                For districts
              </MuiButton>
            </Box>

            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#999', mb: 4 }}>
              Free for teachers &bull; No credit card &bull; FERPA compliant
            </Typography>

            <Box sx={{ borderTop: '1.5px dashed #DDD', pt: 2 }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.65rem', fontWeight: 700, color: '#BBB', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>← </Box>
                Live demos — try them now
                <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}> ↓</Box>
              </Typography>
              {[
                { emoji: '✏️', label: 'Student draft — type a "heart word" for a surprise' },
                { emoji: '🗞️', label: 'Classroom Times — write a headline and publish it' },
                { emoji: '📋', label: 'Assign a lesson — pick a grade, paste any topic' },
                { emoji: '📊', label: 'Class progress — click Sofia to see the AI flag' },
              ].map(item => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                  <Typography sx={{ fontSize: '0.8rem', mt: '1px', flexShrink: 0 }}>{item.emoji}</Typography>
                  <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.78rem', color: '#777', lineHeight: 1.4 }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right: interactive desktop (md+) */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative', height: 720 }}>
            {mounted && <Desktop />}
          </Box>

          {/* Mobile: stacked, touch-friendly demos (xs only) */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {mounted && <MobileDemoStack />}
          </Box>
        </Box>
      </Box>

      {/* Social proof — trusted by 100+ districts */}
      <SocialProofBar />

      {/* Bottom CTA */}
      <Box sx={{ backgroundColor: '#2C2C2C', borderTop: '2px solid #1e1e1e', py: { xs: 8, md: 10 }, px: { xs: 3, md: 6 }, textAlign: 'center' }}>
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h2" sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800, color: '#fff', mb: 2 }}>
            Give your students a real reason to write.
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.05rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, mb: 5 }}>
            Align any lesson. Scaffold every student. Flag AI behavior. Publish a newspaper. One platform, set up in under 10 minutes.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <MuiButton variant="contained" onClick={() => { track(Events.ClickedCTA, { location: 'bottom_v4', label: 'Get Started Free', destination: '/register' }); navigate('/register'); }} sx={{ backgroundColor: '#FFE082', color: '#2C2C2C', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: '6px', border: '2px solid #1e1e1e', px: 5, py: 1.5, boxShadow: '4px 4px 0 rgba(255,255,255,0.2)', '&:hover': { backgroundColor: '#FFD54F', transform: 'translate(2px,2px)' }, transition: 'all 100ms' }}>
              Get Started Free
            </MuiButton>
            <MuiButton variant="outlined" onClick={() => { track(Events.ClickedCTA, { location: 'bottom_v4', label: 'Contact us', destination: '/contact' }); navigate('/contact'); }} sx={{ borderColor: '#fff', border: '2px solid #fff', color: '#fff', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: '6px', px: 5, py: 1.5, boxShadow: '4px 4px 0 rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', transform: 'translate(2px,2px)' }, transition: 'all 100ms' }}>
              Contact us
            </MuiButton>
          </Box>
        </Box>
      </Box>
    </LandingLayout>
  );
}
